import axios from 'axios';
import { showAlert } from './helpers';

/**
 * Reads the CSRF token from local storage without throwing when the user is
 * signed out. Mirrors `readToken` in plugins/http.js, which the newer HTTP
 * layer already uses.
 *
 * @returns {string|null} The token, or null when there is no session.
 */
const readToken = () => {
  try {
    const raw = localStorage.getItem('logged_in_qram');
    if (!raw) return null;
    return JSON.parse(raw)?.token || null;
  } catch (error) {
    return null;
  }
};

/**
 * Wraps an API error body in a real Error.
 *
 * The backend reports failures as a plain object (`{ code, message, name,
 * status, type }`). Rejecting that object as-is caused two problems: Sentry
 * logged it as "Object captured as promise rejection" with no stack trace, and
 * `beforeSend` in plugins/sentry.js could not filter it, because that filter
 * looks for `response.status`. An expired session therefore reported every
 * in-flight request as an unhandled error.
 *
 * `ApiError` in lib/http/ApiError.js solves the same problem for the newer HTTP
 * layer; this keeps the legacy fetcher consistent with it.
 *
 * Every field of the original payload is copied onto the error, so callers that
 * read `error.code`, `error.status` or `error.message` keep working.
 *
 * @param {Object} payload - Error body sent by the server.
 * @param {Error} [cause] - Underlying axios error.
 * @returns {Error} The payload as a throwable error.
 */
const toApiError = (payload, cause = null) => {
  const message =
    payload?.message ||
    payload?.message_id ||
    payload?.message_en ||
    cause?.message ||
    'API request failed';

  const error = new Error(message);
  Object.assign(error, payload);

  /**
   * `message` is a non-enumerable own property on Error, so it would disappear
   * from `{ ...error }` and `JSON.stringify(error)` - both of which used to see
   * it when this was a plain object.
   */
  Object.defineProperty(error, 'message', {
    value: message,
    enumerable: true,
    writable: true,
    configurable: true,
  });

  error.name = payload?.name || 'ApiError';
  error.response = cause?.response || {
    status: payload?.status,
    data: payload,
  };

  return error;
};

export const handleApiError = (ctx, error) => {
  if (error.response) {
    if (error.response.data?.code === 302 || error.response.data?.code === '302') {
      const alertMessage = localStorage.getItem('language_qram') == null 
        ? error.response.data.message_id 
        : error.response.data.message_en;
      showAlert(ctx, alertMessage, 'mdi-alert', 'red');
      ctx.commit('setErrors', error.response.data.data);
      return;
    }
    
    if (error.response.data?.code === 300) {
      ctx.commit('setErrors', error.response.data.data);
    }
    if (error.response.data?.code === 2) {
      showAlert(ctx, error.response.data.message, 'mdi-alert', 'red');
    }
    if (error.response.status === 403) {
      showAlert(ctx, error.response.data.message || error.response.data.message_en, 'mdi-alert', 'red');
      ctx.dispatch('logout', null, { root: true });
    } else if (error.response.status === 401) {
      ctx.dispatch('logout', null, { root: true });
    }
  } else {
    console.error('API Error:', error.message);
  }
};

export const postApi = async (ctx, url, data = null, addConfig = null) => {
  ctx.commit('setLoading', true);

  return new Promise((resolve, reject) => {
    try {
      const token = readToken();

      /**
       * Signing out removes `logged_in_qram`, but requests queued by other
       * components still arrive afterwards. Reading `.token` straight off the
       * missing entry threw `TypeError: ... JSON.parse(...) is null`, which
       * surfaced as an unhandled rejection and looked like a frontend crash
       * rather than the expired session it really is. Reject with the same
       * shape the server would have sent, and skip the request that is certain
       * to fail anyway.
       */
      if (!token) {
        ctx.commit('setLoading', false);
        reject(
          toApiError({
            code: 401,
            status: 401,
            name: 'Unauthenticated',
            message: 'Session has ended. Please sign in again.',
          })
        );
        return;
      }

      const originPath = window.location.pathname;
      const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
      const contentType = addConfig?.contentType ?? (isFormData ? null : 'application/json');
      const baseHeaders = {
        'X-CSRF-TOKEN': token,
        'X-Origin-Path': originPath,
      };

      if (contentType) {
        baseHeaders['Content-Type'] = contentType;
      }

      const additionalHeaders = addConfig?.headers || {};
      const headers = { ...baseHeaders, ...additionalHeaders };

      const config = {
        method: 'post',
        url: process.env.VUE_APP_API_BASE_URL + url,
        headers,
        data,
      };
      axios(config)
        .then((response) => {
          const allowCode = [10, 11, 200, '00', '10', '01', '11', '200', 'A0'];

          if (allowCode.includes(response.data.code)) {
            resolve(response.data);
          } else {
            const notAlert = [302];
            const errorMessage =
              response.data.message_id || response.data.message_en;

            if (!notAlert.includes(response.data.code)) {
              showAlert(ctx, errorMessage, 'mdi-alert', 'red');
            }

            if (response.data.code === 401) {
              ctx.dispatch('logout', null, { root: true });
            }

            if (response.data.code === 302) {
              ctx.commit('setErrors', response.data.data);
              resolve(response.data);
              return;
            }

            resolve(errorMessage);
          }
        })
        .catch((error) => {
          handleApiError(ctx, error);
          if (error.response?.data) {
            reject(toApiError(error.response.data, error));
          } else {
            reject(error);
          }
        })
        .finally(() => {
          ctx.commit('setLoading', false);
        });
    } catch (error) {
      handleApiError(ctx, error);
      reject(error);
      ctx.commit('setLoading', false);
    }
  });
};

export const handleStoreApiError = (ctx, responseData) => {
  ctx.commit('setErrors', responseData?.data || responseData);

  if (localStorage.getItem('language_qram') == null) {
    showAlert(ctx, responseData.message_id, 'mdi-alert', 'red');
  } else {
    showAlert(ctx, responseData.message_en, 'mdi-alert', 'red');
  }
  if (
    typeof responseData.data?.message !== 'object' &&
    responseData.data?.message != null
  ) {
    showAlert(ctx, responseData.data.message, 'mdi-alert', 'red');
  }

  if (responseData.code == '401') {
    ctx.dispatch('logout', null, { root: true });
  }

  if (
    !(
      typeof responseData.data?.message !== 'object' &&
      responseData.data?.message != null
    )
  ) {
    showAlert(ctx, responseData, 'mdi-alert', 'red');
  }
};
